import React, { useState, useEffect, useCallback } from 'react';
import SEO from '../components/SEO';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Calendar, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BLOG_CONTENT_STYLES = "prose max-w-none text-text_muted leading-relaxed [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:text-primary [&_h1]:mb-4 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-primary [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-medium [&_h3]:text-primary [&_h3]:mb-2 [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_li]:mb-1 [&_blockquote]:border-l-4 [&_blockquote]:border-accent [&_blockquote]:pl-4 [&_blockquote]:italic [&_a]:text-accent [&_a]:underline";

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBlog, setSelectedBlog] = useState(null);

  const fetchBlogs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/blogs`);
      setBlogs(data);
    } catch (error) {
      console.error('Blog fetch failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getImageSrc = (url) => {
    if (!url) return null;
    if (url.startsWith('/api')) return `${API_URL}${url}`;
    return url;
  };

  const stripHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  return (
    <div>
      <SEO
        title="Blog & News - RESCO Invictus Green Energy Solutions"
        description="Stay updated with the latest news, insights, and developments in solar energy and renewable power."
        keywords="solar news, renewable energy blog, C&I solar insights, green energy updates"
        url="/blog"
      />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-surface" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-accent mb-4 block" data-testid="blog-overline">
              Insights & Updates
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-primary mb-6" data-testid="blog-title">
              Blog & News
            </h1>
            <p className="text-base leading-relaxed text-text_muted" data-testid="blog-description">
              Stay informed about the latest developments in solar energy, industry trends, and our project updates.
            </p>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-20" data-testid="blog-grid-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          {loading ? (
            <div className="text-center py-20" data-testid="blog-loading">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-text_muted">Loading articles...</p>
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center py-20" data-testid="blog-empty">
              <p className="text-xl text-text_muted">No articles published yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.map((blog, index) => (
                <div
                  key={blog.id}
                  className="bg-white rounded-sm border border-border overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer"
                  onClick={() => setSelectedBlog(blog)}
                  data-testid={`blog-card-${index}`}
                >
                  {blog.featured_image && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={getImageSrc(blog.featured_image)}
                        alt={blog.title}
                        className="w-full h-full object-cover"
                        data-testid={`blog-image-${index}`}
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center text-sm text-text_muted mb-3">
                      <Calendar size={16} className="mr-2 text-accent" />
                      <span data-testid={`blog-date-${index}`}>{formatDate(blog.created_at)}</span>
                    </div>
                    <h3 className="text-xl font-semibold text-primary mb-3" data-testid={`blog-title-${index}`}>
                      {blog.title}
                    </h3>
                    <p className="text-text_muted leading-relaxed line-clamp-3" data-testid={`blog-excerpt-${index}`}>
                      {stripHtml(blog.content).substring(0, 150)}...
                    </p>
                    <button
                      className="mt-4 text-accent font-medium inline-flex items-center hover:underline"
                      data-testid={`blog-read-more-${index}`}
                    >
                      Read More
                      <ArrowRight size={16} className="ml-1" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Blog Modal */}
      {selectedBlog && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedBlog(null)}
          data-testid="blog-modal"
        >
          <div
            className="bg-white rounded-sm max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {selectedBlog.featured_image && (
              <img
                src={getImageSrc(selectedBlog.featured_image)}
                alt={selectedBlog.title}
                className="w-full h-64 object-cover"
                data-testid="blog-modal-image"
              />
            )}
            <div className="p-8">
              <div className="flex items-center text-sm text-text_muted mb-4">
                <Calendar size={16} className="mr-2 text-accent" />
                <span data-testid="blog-modal-date">{formatDate(selectedBlog.created_at)}</span>
              </div>
              <h2 className="text-3xl font-bold text-primary mb-6" data-testid="blog-modal-title">
                {selectedBlog.title}
              </h2>
              <div 
                className={BLOG_CONTENT_STYLES}
                data-testid="blog-modal-content"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedBlog.content) }}
              />
              <button
                onClick={() => setSelectedBlog(null)}
                className="mt-8 px-6 py-3 bg-accent text-white rounded font-semibold hover:bg-opacity-90 transition-all"
                data-testid="blog-modal-close"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Blog;
